import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, Button, Divider, Box } from '@mui/material';
import { statusLabelMap } from 'src/modules/nurse/isolation/request-list/pages/list/models/enums';
import { useTranslate } from 'src/locales';
import IsolationFinishConfirmDialog from './IsolationFinishConfirmDialog';

interface IsolationActionModalProps {
  open: boolean;
  onClose: () => void;
  isolationName: string;
  onAction: (action: 'gonder' | 'bitir' | 'gerial' | 'sil', newStatus?: string) => void;
  status?: string;
}

const IsolationActionModal: React.FC<IsolationActionModalProps> = ({ open, onClose, isolationName, onAction, status }) => {
  const { t } = useTranslate();
  const initialDurum = statusLabelMap[status ?? "0"] || 'Beklemede';
  const [localDurum, setLocalDurum] = useState<string>(initialDurum);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);

  useEffect(() => {
    setLocalDurum(statusLabelMap[status ?? "0"] || 'Beklemede');
  }, [status, open]);

  const canSend = localDurum === 'Beklemede';
  const canDelete = localDurum === 'Beklemede';
  const canFinish = localDurum === 'Onaylandı';
  const canRollback = localDurum === 'Gönderildi';

  const handleFinishConfirm = async (): Promise<boolean> => {
    try {
      setLocalDurum('Onaylandı');
      onAction('bitir', '4');
      onClose(); // Close the action modal after the action is initiated
      return true;
    } catch (error) {
      console.error('Finish isolation error:', error);
      return false;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* İzolasyon İsteğini Gönder */}
            <Button
              variant="text"
              onClick={() => {
                if (!canSend) return;
                setLocalDurum('Gönderildi');
                onAction('gonder', '1');
                onClose();
              }}
              disabled={!canSend}
              sx={{
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                color: canSend ? 'text.primary' : 'text.disabled',
              }}
            >
              İzolasyon İsteğini Gönder
            </Button>

            {/* İzolasyon İsteğini Sil */}
            <Button
              variant="text"
              onClick={() => {
                if (!canDelete) return;
                setLocalDurum('Beklemede');
                onAction('sil', '3');
                onClose();
              }}
              disabled={!canDelete}
              sx={{
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                color: canDelete ? 'text.primary' : 'text.disabled',
              }}
            >
              İzolasyon İsteğini Sil
            </Button>

            <Divider />

            {/* Bitir */}
            <Button
              variant="text"
              onClick={() => {
                if (!canFinish) return;
                setFinishConfirmOpen(true);
              }}
              disabled={!canFinish}
              sx={{
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                color: canFinish ? 'text.primary' : 'text.disabled',
              }}
            >
              Bitir
            </Button>

            {/* Geri Al */}
            <Button
              variant="text"
              onClick={() => {
                if (!canRollback) return;
                setLocalDurum('Beklemede');
                onAction('gerial', '0');
                onClose();
              }}
              disabled={!canRollback}
              sx={{
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                color: canRollback ? 'text.primary' : 'text.disabled',
              }}
            >
              Geri Al
            </Button>

            <Divider />

            {/* Kapat */}
            <Button
              variant="text"
              onClick={onClose}
              sx={{
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                color: 'primary.main',
              }}
            >
              Kapat
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <IsolationFinishConfirmDialog
        open={finishConfirmOpen}
        onClose={() => setFinishConfirmOpen(false)}
        onConfirm={handleFinishConfirm}
        title={t("isolation-requests.finish-confirm-title") || "Uyarı Mesajı"}
        content={t("isolation-requests.finish-confirm-content") || "İzolasyon süreci tamamlanacak. Devam etmek istiyor musunuz?"}
      />
    </>
  );
};

export default IsolationActionModal; 